import type { PersonaId } from "@/config/personas";
import type { ActionReturnType } from "../types";

/**
 * Demo-only stand-ins for backend features that do not exist yet (the `dev` showcase,
 * N7AIT-1419). Every method answers like an endpoint would, so wiring one to the real
 * backend means replacing its body with a `request()`. Each value it returns is shown
 * next to a `MockBadge`; drop the badge with the method.
 */

export type DemoPreferences = { persona: PersonaId | null };

export type ErrorCategoryCount = { key: "application" | "agent" | "infrastructure"; count: number };

export type ManualBaseline = {
  /** How many times longer a person takes to run the same test by hand. */
  multiplier: number;
  hourlyRate: number;
  currency: string;
};

export type ReleaseVerdict = "ready" | "at_risk" | "blocked";
export type ReleaseReadiness = { version: string; passRate: number; blockers: number; verdict: ReleaseVerdict };

export type JiraBugStats = { reported: number; open: number; resolved: number; critical: number };

/** Stands in for a per-user preference the backend would keep (`GET /users/me/preferences/`). */
const PERSONA_BY_EMAIL: Record<string, PersonaId> = {
  "ceo@example.com": "executive",
  "sales@example.com": "sales",
  "qa@example.com": "qa",
  "api@example.com": "api",
};

const ok = <T>(data: T) => Promise.resolve({ ok: true as const, data });

export class DemoEndpoint {
  public preferences(email: string): ActionReturnType<DemoPreferences> {
    return ok({ persona: PERSONA_BY_EMAIL[email.toLowerCase()] ?? null });
  }

  /**
   * What a classified failure split could look like. Today 77% of failures come back
   * `unknown` (N7AIT-1408), so the real total is divided by fixed shares instead.
   */
  public errorCategories(failedRuns: number): ActionReturnType<ErrorCategoryCount[]> {
    const application = Math.round(failedRuns * 0.52);
    const agent = Math.round(failedRuns * 0.29);
    return ok([
      { key: "application", count: application },
      { key: "agent", count: agent },
      { key: "infrastructure", count: Math.max(0, failedRuns - application - agent) },
    ]);
  }

  public manualBaseline(): ActionReturnType<ManualBaseline> {
    return ok({ multiplier: 4, hourlyRate: 35, currency: "USD" });
  }

  public releaseReadiness(): ActionReturnType<ReleaseReadiness[]> {
    return ok([
      { version: "4.12.0 (build 1882)", passRate: 0.97, blockers: 0, verdict: "ready" },
      { version: "4.11.2 (build 1859)", passRate: 0.91, blockers: 2, verdict: "at_risk" },
      { version: "4.11.1 (build 1840)", passRate: 0.78, blockers: 5, verdict: "blocked" },
    ]);
  }

  public jiraBugs(): ActionReturnType<JiraBugStats> {
    return ok({ reported: 46, open: 12, resolved: 34, critical: 3 });
  }
}
